import React, { lazy, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import AccessibleNavigationAnnouncer from "./components/AccessibleNavigationAnnouncer";
import packageJson from "../package.json";
import PrivateRoute from "./privateRoute";
import IntercomLoader from "./components/IntercomeLoader"; // Adjust the path as necessary
import ReferralCapture from "./components/ReferralCapture"; // Partnero referral tracking

import MaintenanceMode from "./components/MaintenanceMode";
// Not lazy: the sign-in page is the app's front door, and the template
// shows it at once. A lazy chunk put the loading screen in front of it, so
// the page's own entrance animation ran only after a spinner (owner
// feedback 2026-09-29).
import Login from "./pages/Login";

global.appVersion = packageJson.version;
const Layout = lazy(() => import("./containers/Layout"));
const NotFound = lazy(() => import("./pages/404"));
const CreateAccount = lazy(() => import("./pages/CreateAccount"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const SignupSuccess = lazy(() => import("./pages/SignupSuccess"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const Impersonate = lazy(() => import("./pages/Impersonate"));
const Connect = lazy(() => import("./pages/Connect"));

const semverGreaterThan = (versionA, versionB) => {
  const versionsA = versionA.split(/\./g);

  const versionsB = versionB.split(/\./g);
  while (versionsA.length || versionsB.length) {
    const a = Number(versionsA.shift());

    const b = Number(versionsB.shift());
    // eslint-disable-next-line no-continue
    if (a === b) continue;
    // eslint-disable-next-line no-restricted-globals
    return a > b || isNaN(b);
  }
  return false;
};

/** Preserve ?ref= (and other query params) so PartneroJS can register clicks. */
function RootRedirect() {
  const { search } = useLocation();
  const hasRef = new URLSearchParams(search).has("ref");
  const destination = hasRef ? "/create-account" : "/login";
  return <Navigate to={`${destination}${search}`} replace />;
}

function App() {
  const [isMaintenanceMode] = useState(false);

  useEffect(() => {
    fetch("/meta.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to fetch meta.json");
        }
        return response.json();
      })
      .then((meta) => {
        const latestVersion = meta.version;
        const currentVersion = global.appVersion;

        const shouldForceRefresh = semverGreaterThan(
          latestVersion,
          currentVersion,
        );
        if (shouldForceRefresh) {
          console.log(
            `We have a new version - ${latestVersion}. Should force refresh`,
          );

          caches.keys().then(function (names) {
            Promise.all(names.map((name) => caches.delete(name)))
              .then(() => {
                console.log("All caches cleared, reloading page.");
                window.location.reload(true); // Force reload from server
              })
              .catch((error) => {
                console.error("Error clearing caches:", error);
              });
          });
        } else {
          console.log(
            `You already have the latest version - ${latestVersion}. No cache refresh needed.`,
          );
        }
      })
      .catch((error) => {
        console.error("Error fetching meta.json:", error);
      });
  }, []);

  return (
    <Router>
      <AccessibleNavigationAnnouncer />
      <IntercomLoader />
      <ReferralCapture />
      {isMaintenanceMode ? (
        <MaintenanceMode />
      ) : (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/impersonate" element={<Impersonate />} />
          {/* An AI app asks to connect (agent server sign-in). Outside /app:
              it handles a signed-out visitor itself. */}
          <Route path="/connect" element={<Connect />} />
          <Route path="/create-account" element={<CreateAccount />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/signup-success" element={<SignupSuccess />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          <Route path="/" element={<RootRedirect />} />

          {/* Protected routes */}
          <Route path="/app/*" element={<PrivateRoute Component={Layout} />} />

          {/* Anything else outside /app: a real not-found page, not a blank
              screen (unknown /app paths are handled inside the layout). */}
          <Route path="*" element={<NotFound standalone />} />
        </Routes>
      )}
    </Router>
  );
}

export default App;
